// Auditoria 3 - Item 10: generacion de Boarding Pass en PDF con codigo QR desde Mostrador.
// Client-side, sin carga en backend. Se llama con datos del pasajero, vuelo y asiento.
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

/**
 * Genera y descarga un Boarding Pass en PDF con codigo QR embebido.
 *
 * @param {object} args
 * @param {object} args.passenger - { first_name, last_name, document_number, ticket_number }
 * @param {object} args.flight    - { flight_number, origin, destination, scheduled_departure, scheduled_arrival, aircraft_registration }
 * @param {string} args.seat      - asiento asignado, ej "12A"
 * @param {string} [args.pnr]     - PNR de la reserva (opcional; si viene, se prioriza)
 * @param {string} [args.airline] - nombre de la aerolinea (para header)
 * @param {string} [args.qrPayload] - payload JSON EXACTO a codificar en el QR. Si viene, se
 *                                    respeta tal cual (para reimpresiones). Si no, se genera.
 * @param {number} [args.reprintCount] - si > 0, se estampa "REIMPRESO" en el pase.
 * @returns {Promise<{qrPayload: string, filename: string}>} — el payload usado (util para
 *   guardar en el historial del backend en la primera emision).
 */
export const generateBoardingPass = async ({ passenger, flight, seat, pnr, airline, qrPayload, reprintCount, gate, boardingTime, boardingGroup, sequenceNumber, cabinClass, originIata, destinationIata }) => {
    if (!passenger || !flight) throw new Error('passenger y flight son requeridos');

    const pnrValue = pnr || passenger.ticket_number || `AO-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    const passengerName = `${(passenger.first_name || '').trim()} ${(passenger.last_name || '').trim()}`.trim() || 'Pasajero';
    const flightNumber = flight.flight_number || '—';
    const dep = flight.scheduled_departure || '';
    const dateStr = dep ? dep.split('T')[0] : '';
    const timeStr = dep ? (dep.split('T')[1] || '').slice(0, 5) : '--:--';
    // Auditoria 4 - Item 10: mostrar IATA (3 letras) al pasajero, no ICAO. Fallback a ICAO si no hay iata.
    const origin = (originIata || flight.origin || '—').toUpperCase();
    const destination = (destinationIata || flight.destination || '—').toUpperCase();
    // Auditoria 4 - Item 9: campos extra
    const boardingT = boardingTime || flight.boarding_time || '';
    const boardingTimeStr = boardingT ? (typeof boardingT === 'string' ? boardingT.split('T')[1]?.slice(0, 5) || boardingT.slice(0, 5) : '') : '';
    const gateStr = gate || flight.gate || '—';
    const groupStr = boardingGroup || '';
    const seqStr = sequenceNumber ? String(sequenceNumber).padStart(3, '0') : '';
    const cabinStr = (cabinClass || 'ECONOMY').toUpperCase();

    // 1) Contenido codificado en el QR: si vino qrPayload lo usamos tal cual (reimpresion).
    const finalQrPayload = qrPayload || JSON.stringify({
        pnr: pnrValue,
        name: passengerName,
        flight: flightNumber,
        date: dateStr,
        seat: seat || '',
        doc: passenger.document_number || '',
    });
    const qrDataUrl = await QRCode.toDataURL(finalQrPayload, { width: 200, margin: 1, errorCorrectionLevel: 'M' });

    // 2) PDF apaisado tamaño boleto (200 x 100 mm)
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [200, 100] });

    // Header
    doc.setFillColor(15, 23, 42); // slate-900
    doc.rect(0, 0, 200, 18, 'F');
    doc.setTextColor(245, 158, 11); // amber-500
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text((airline || 'AEROOPS').toUpperCase(), 8, 12);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text('BOARDING PASS', 195, 12, { align: 'right' });

    // Cuerpo
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('PASAJERO', 8, 28);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(passengerName.toUpperCase(), 8, 34);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('VUELO', 8, 44);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(flightNumber, 8, 54);

    // Ruta grande — separador visual sin caracteres Unicode (jsPDF default no soporta →).
    // Auditoria 4 - Item 8: reemplazar '→' por triangulo dibujado + linea.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.text(origin, 8, 72);
    // Linea + triangulo (flecha) como forma vectorial, no como texto
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(0.8);
    doc.line(38, 68, 46, 68); // linea horizontal
    doc.setFillColor(15, 23, 42);
    // Triangulo relleno como punta de flecha (x1,y1, x2,y2, x3,y3, style)
    doc.triangle(46, 65, 46, 71, 50, 68, 'F');
    doc.setFontSize(24);
    doc.text(destination, 52, 72);

    // Fecha, hora, asiento, PNR, GATE, BOARDING, GROUP, SEQ (Auditoria 4 Item 9)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text('FECHA', 8, 82);
    doc.text('HORA', 32, 82);
    doc.text('ASIENTO', 52, 82);
    doc.text('PNR', 74, 82);
    doc.text('GATE', 100, 82);
    doc.text('BOARDING', 116, 82);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(dateStr || '—', 8, 88);
    doc.text(timeStr, 32, 88);
    doc.text(String(seat || '—').toUpperCase(), 52, 88);
    doc.text(pnrValue, 74, 88);
    doc.text(String(gateStr).toUpperCase(), 100, 88);
    doc.text(boardingTimeStr || '—', 116, 88);
    // Segunda fila: clase, grupo, sequencia, aeronave
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text(`CLASE ${cabinStr}`, 8, 94);
    if (groupStr) doc.text(`GRUPO ${groupStr}`, 40, 94);
    if (seqStr) doc.text(`SEQ ${seqStr}`, 62, 94);
    if (flight.aircraft_registration) {
        doc.text(`AC: ${flight.aircraft_registration}`, 82, 94);
    }
    doc.setTextColor(15, 23, 42);

    // QR a la derecha
    try {
        doc.addImage(qrDataUrl, 'PNG', 145, 25, 48, 48);
    } catch (e) {
        // fallback: si addImage falla, escribir texto
        doc.setFontSize(7);
        doc.text('QR no disponible', 155, 60);
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text('Presente este pase en el embarque', 145, 78);
    doc.text(`Generado: ${new Date().toLocaleString('es-ES')}`, 145, 82);
    if (reprintCount && reprintCount > 0) {
        // Estampa "REIMPRESO N" visible pero sin invalidar el pase
        doc.setTextColor(180, 83, 9); // amber-700
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(`REIMPRESION #${reprintCount}`, 145, 88);
    }

    // Borde
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.rect(4, 22, 192, 74);

    // Guardar
    const filename = `boarding_${flightNumber}_${(seat || 'seat').toUpperCase()}_${passengerName.split(' ')[0]}.pdf`;
    doc.save(filename);
    return { qrPayload: finalQrPayload, filename };
};

export default generateBoardingPass;
